/**
 * AR-390 — the Settings module's pages, in one place.
 *
 * Settings is a Level 2 module (the same pattern as Account and Finances),
 * opened from the user menu directly under Account. v15 (2026-09-18) split it
 * out of the Account center: D-ACCT-27 made the Platform group, D-ACCT-28 made
 * it a module of its own, and D-ACCT-38 settled its column at FOUR pages in
 * every phase — Color Theme, AIVIA Settings, CRM Settings, Website Settings.
 *
 * Unlike Account, the column keeps its heading: there is no identity block
 * above it to name the module.
 *
 * The nav column, the router and the settings-search index all read this file,
 * so a page cannot exist in one and be missing from another.
 */

/** The pages in the Level 2 column, in column order. */
export const SETTINGS_PAGE_IDS = ['theme', 'aivia', 'crm', 'website'] as const;

/**
 * D-ACCT-38 — Proposal Builder and Payments + CardVault are PARKED. Their
 * pages still render, so an old link and a bookmark both still work, but they
 * are out of the column and out of the settings search. Products is retired
 * outright; `/settings/products` lands on CRM Settings, which carries its
 * Phase-1 way in.
 */
export const SETTINGS_PARKED_PAGE_IDS = ['proposals', 'payments'] as const;

export type SettingsPageId =
  | (typeof SETTINGS_PAGE_IDS)[number]
  | (typeof SETTINGS_PARKED_PAGE_IDS)[number];

/** Every page that renders, column and parked alike — what the router walks. */
export const SETTINGS_ALL_PAGE_IDS: readonly SettingsPageId[] = [
  ...SETTINGS_PAGE_IDS,
  ...SETTINGS_PARKED_PAGE_IDS,
];

export const settingsPageIsParked = (page: SettingsPageId): boolean =>
  (SETTINGS_PARKED_PAGE_IDS as readonly string[]).includes(page);

const BASE = '/settings';

/**
 * Every flag the module needs, parent first — the sub-flag is meaningless
 * without it, the same pair the Account center uses. The routes, the user-menu
 * row and the `/settings` redirect ALL read this one constant: kept apart they
 * drift, and a menu row pointing at a gated-off page is a dead end.
 */
export const SETTINGS_MODULE_FLAGS = [
  'proMigration',
  'proMigrationSettings',
] as const;

export const SETTINGS_PATHS: Record<SettingsPageId, string> = {
  theme: `${BASE}/theme`,
  aivia: `${BASE}/aivia`,
  crm: `${BASE}/crm`,
  website: `${BASE}/website`,
  proposals: `${BASE}/proposals`,
  payments: `${BASE}/payments`,
};

/** `/settings` is the module home; it lands on Color Theme. */
export const SETTINGS_HOME = BASE;
export const SETTINGS_HOME_PAGE: SettingsPageId = 'theme';

/**
 * The retired Products page. An old link lands on CRM Settings rather than on
 * a hold card — the treatment the prototype's `route()` gives it.
 */
export const SETTINGS_RETIRED_PATHS: Record<string, string> = {
  [`${BASE}/products`]: SETTINGS_PATHS.crm,
};

/** Title and standfirst, as the prototype's AC_TITLES prints them. */
export const SETTINGS_PAGE_TITLES: Record<
  SettingsPageId,
  readonly [string, string]
> = {
  theme: [
    'Color Theme',
    'The color theme, display mode and text size you see across the platform.',
  ],
  aivia: [
    'AIVIA Settings',
    'Your notifications, email, and how AIVIA writes and works with your data.',
  ],
  crm: [
    'CRM Settings',
    "Your CRM's version, email connection, workflow defaults and data.",
  ],
  website: [
    'Website Settings',
    'Your WorldVia Sites website and Dynamic Promotions, each managed in its own app.',
  ],
  proposals: [
    'Proposal Builder',
    'What every proposal says by default, and what the client sees.',
  ],
  payments: [
    'Payments + CardVault',
    'Your encryption key, the cards in your vault, and the payment email.',
  ],
};

/** Short label for a page in the Level 2 column. */
export const SETTINGS_NAV_LABELS: Record<SettingsPageId, string> = {
  theme: 'Color Theme',
  aivia: 'AIVIA Settings',
  crm: 'CRM Settings',
  website: 'Website Settings',
  proposals: 'Proposal Builder',
  payments: 'Payments + CardVault',
};

/** The column's own heading — Account has an identity block instead. */
export const SETTINGS_COLUMN_HEADING = 'Settings';

/**
 * The migration phase this build serves. In Phase 1 the CRM still runs on
 * WorldVia PRO, so CRM Settings is one card with the door into it; the native
 * page arrives in Phase 2. One predicate, so the page and its search entries
 * cannot describe two different pages.
 */
export const SETTINGS_PHASE = 1;
export const settingsCrmIsNative = (): boolean => SETTINGS_PHASE >= 2;

/**
 * The sprint this build serves, and the cards a later sprint carries.
 * D-ACCT-08 and D-ACCT-09 schedule the notification matrix, email
 * subscriptions, the AIVIA assistance level and AIVIA data + privacy at sprint
 * 2; before it each card takes the not-yet treatment rather than offering a
 * switch nobody can set.
 */
export const SETTINGS_SPRINT = 1;
export const SETTINGS_CARDS_FROM_SPRINT: Record<string, number> = {
  notifications: 2,
  email: 2,
  assist: 2,
  aidata: 2,
};

export const settingsCardInSprint = (card: string): boolean =>
  (SETTINGS_CARDS_FROM_SPRINT[card] ?? 1) <= SETTINGS_SPRINT;

/**
 * Settings Route `path` props in App.tsx — must stay synchronized with
 * SETTINGS_PATHS. The module home is NOT here: `/settings` keeps its legacy
 * route and redirects into the module while the flags are on, the same way
 * `/profile` hands over to the Account center.
 */
export const SETTINGS_APP_ROUTE_PATHS = [
  ...SETTINGS_ALL_PAGE_IDS.map((id) => SETTINGS_PATHS[id]),
  ...Object.keys(SETTINGS_RETIRED_PATHS),
];

export const isSettingsPath = (location: string): boolean =>
  location === BASE || location.startsWith(`${BASE}/`);

/** The page id a location resolves to, or null when it is outside the module. */
export function settingsPageIdFor(location: string): SettingsPageId | null {
  const hit = SETTINGS_ALL_PAGE_IDS.find((id) => SETTINGS_PATHS[id] === location);
  return hit ?? null;
}
